// src/utils/idle.js
// Idle timeout manager with cross-tab last-activity timestamp in localStorage
// - Shows warning via callback ~1 minute before logout
// - Logs out after 20 minutes of inactivity

const IDLE_MS = (Number(process.env.VUE_APP_IDLE_MINUTES) || 20) * 60 * 1000;       // default 20 minutes
const WARNING_MS = (Number(process.env.VUE_APP_IDLE_WARNING_SECONDS) || 60) * 1000; // default 60 seconds
const KEY_LAST = 'lastActivityAt';

let lastActivity = Date.now();
let warningShown = false;
let warningTimer = null;
let checkTimer = null;
let onWarn = () => { };
let onLogout = () => { };
let onStay = () => { };

function now() {
  return Date.now();
}

function setLastActivity(ts = now(), broadcast = true) {
  lastActivity = ts;
  if (broadcast) {
    try {
      localStorage.setItem(KEY_LAST, String(ts));
    } catch {
      // Silently ignore localStorage errors (e.g., private browsing mode)
    }
  }
}

function handleUserActivity() {
  setLastActivity();
  if (warningShown) {
    warningShown = false;
    if (onStay) onStay();
  }
}

function attachActivityListeners() {
  ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((ev) => {
    window.addEventListener(ev, handleUserActivity, { passive: true });
  });
}

function detachActivityListeners() {
  ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((ev) => {
    window.removeEventListener(ev, handleUserActivity);
  });
}

function attachStorageListener() {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY_LAST && e.newValue) {
      const ts = Number(e.newValue);
      if (!Number.isNaN(ts) && ts > lastActivity) {
        lastActivity = ts;
      }
    }
  });
}

function startLoop() {
  if (checkTimer) clearInterval(checkTimer);
  checkTimer = setInterval(() => {
    const t = now();
    const inactiveFor = t - lastActivity; // relies on timestamp; robust vs suspended tabs/sleep
    // Show warning once when entering the final minute window
    if (!warningShown && inactiveFor >= (IDLE_MS - WARNING_MS) && inactiveFor < IDLE_MS) {
      warningShown = true;
      const secondsLeft = Math.ceil((IDLE_MS - inactiveFor) / 1000);
      if (onWarn) onWarn({ secondsLeft });
      if (warningTimer) clearTimeout(warningTimer);
      warningTimer = setTimeout(() => {
        const stillInactive = (now() - lastActivity) >= (IDLE_MS - WARNING_MS);
        if (stillInactive && onLogout) onLogout();
      }, (IDLE_MS - inactiveFor));
    }
    // If somehow we pass IDLE_MS without warning (tab resumed late), logout immediately
    if (inactiveFor >= IDLE_MS) {
      if (onLogout) onLogout();
    }
  }, 5000);
}

export const IdleManager = {
  init({ onWarnModal, onAutoLogout, onStayLoggedIn } = {}) {
    onWarn = onWarnModal || (() => { });
    onLogout = onAutoLogout || (() => { });
    onStay = onStayLoggedIn || (() => { });
    // Initialize last activity with persisted timestamp if present
    try {
      const persisted = Number(localStorage.getItem(KEY_LAST));
      if (!Number.isNaN(persisted) && persisted > 0) lastActivity = persisted;
      else setLastActivity(now(), false);
    } catch {
      setLastActivity(now(), false);
    }
    attachActivityListeners();
    attachStorageListener();
    startLoop();
  },
  signalActivity() {
    setLastActivity();
  },
  destroy() {
    detachActivityListeners();
    if (checkTimer) clearInterval(checkTimer);
    if (warningTimer) clearTimeout(warningTimer);
  }
};
